import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';

function Home() {
  const [products, setProducts] = useState([]);
  
  useEffect(() => {
    fetchProducts();
  }, []);
  
  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data.slice(0, 6)); // Show only 6 products on home
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };
  
  return (
    <div className="min-h-screen">
      {/* Hero Section with Video */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Video */}
        <div className="absolute inset-0">
          <video 
            autoPlay 
            loop 
            muted 
            playsInline
            className="w-full h-full object-cover"
            style={{ filter: 'brightness(0.7)' }}
          >
            <source src="https://customer-assets.emergentagent.com/job_nanox-connect/artifacts/jw8t4sw8_grok-video-91e68491-e7c5-4d85-9221-7fe646496da2%20%281%29.mp4" type="video/mp4" />
          </video>
          {/* Lighter blue overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-950/60 via-slate-950/60 to-cyan-950/60"></div>
        </div>
        
        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="animate-fade-in">
            <img 
              src="https://customer-assets.emergentagent.com/job_nanox-connect/artifacts/v7gkh85r_Gemini_Generated_Image_oay1wpoay1wpoay1.png" 
              alt="M31 NanoX" 
              className="w-64 h-64 mx-auto mb-8 rounded-3xl shadow-2xl shadow-cyan-500/20"
              data-testid="hero-logo"
            />
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-300">
              M31 NanoX
            </h1>
            <p className="text-2xl md:text-3xl text-cyan-300 mb-8 font-light">
              Materials for Future Technologies
            </p>
            <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto">
              Leading provider of advanced nanomaterials and battery materials powering the next generation of innovation
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/catalogue"
                data-testid="hero-catalogue-button"
                className="px-8 py-4 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-cyan-500/50 transition-all transform hover:scale-105"
              >
                Explore Catalogue
              </Link>
              <Link
                to="/contact"
                data-testid="hero-contact-button"
                className="px-8 py-4 bg-slate-800/50 backdrop-blur-sm text-white font-semibold rounded-xl border border-cyan-500/50 hover:bg-slate-700/50 transition-all"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
        
        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <svg className="w-6 h-6 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Why Choose M31 NanoX?</h2>
            <p className="text-xl text-gray-400">Cutting-edge materials for cutting-edge applications</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 p-8 rounded-2xl border border-blue-500/20 hover:border-blue-500/50 transition-all" data-testid="feature-quality">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Premium Quality</h3>
              <p className="text-gray-400">Ultra-high purity materials with rigorous quality control and certification</p>
            </div>
            
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 p-8 rounded-2xl border border-cyan-500/20 hover:border-cyan-500/50 transition-all" data-testid="feature-innovation">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Innovation Driven</h3>
              <p className="text-gray-400">State-of-the-art nanomaterials enabling breakthrough technologies</p>
            </div>
            
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 p-8 rounded-2xl border border-blue-500/20 hover:border-blue-500/50 transition-all" data-testid="feature-support">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Expert Support</h3>
              <p className="text-gray-400">Dedicated technical support and customization services</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Products Preview */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Featured Products</h2>
            <p className="text-xl text-gray-400">Explore our range of advanced materials</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product) => (
              <div key={product.id} className="bg-slate-800/50 backdrop-blur-sm rounded-2xl overflow-hidden border border-slate-700 hover:border-cyan-500/50 transition-all group" data-testid={`product-card-${product.id}`}>
                <div className="h-48 overflow-hidden">
                  <img 
                    src={product.image_url} 
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="text-xs font-semibold text-cyan-400 mb-2 uppercase">
                    {product.category === 'nanomaterials' ? 'Nanomaterials' : 'Battery Materials'}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{product.name}</h3>
                  <p className="text-gray-400 text-sm line-clamp-2">{product.description}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link
              to="/catalogue"
              data-testid="view-all-products-button"
              className="inline-block px-8 py-4 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-cyan-500/50 transition-all transform hover:scale-105"
            >
              View All Products
            </Link>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-900/20 via-cyan-900/20 to-blue-900/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Innovate?</h2>
          <p className="text-xl text-gray-300 mb-8">
            Get in touch with our team to discuss your material requirements
          </p>
          <Link
            to="/contact"
            data-testid="cta-contact-button"
            className="inline-block px-10 py-4 bg-white text-blue-900 font-bold rounded-xl hover:bg-gray-100 transition-all transform hover:scale-105 shadow-xl"
          >
            Contact Our Team
          </Link>
        </div>
      </section>
    </div>
  );
}

export default Home;